import React, { useState, useEffect } from "react";
import { Info, Search, X } from "lucide-react";
import CollapsibleItemsList from "../../ui/CollapsibleItemsList.tsx";
import InfoBox from "../../ui/InfoBox.tsx";
import AssociationItem from "./AssociationItem.tsx";
import Pagination from "../../ui/Pagination.tsx";

interface Association {
  user_id: string;
  profile_id: string;
}

interface Profile {
  profile_id: string;
  backend_config?: {
    collections?: string[];
    prompts?: any[];
  };
}

interface PaginationState {
  currentPage: number;
  hasNextPage: boolean;
  lastEvaluatedKey: string | null;
  isLoading: boolean;
}

interface AssociationFilters {
  profile_id?: string;
  limit?: number;
}

interface AssociationViewTabProps {
  associations: Association[];
  isLoading: boolean;
  isMobile: boolean;
  isDarkMode: boolean;
  pagination: PaginationState;
  filters: AssociationFilters;
  profiles: Profile[];
  totalPages?: number;
  totalAssociations?: number;
  searchUserId?: string;
  onDeleteAssociation: (userId: string) => Promise<void>;
  onPageChange: (page: number) => void;
  onFilterChange: (filters: AssociationFilters) => void;
  onClearFilters: () => void;
  onSearchByUser: (userId: string) => void;
}

const AssociationViewTab = ({
  associations,
  isLoading,
  isMobile,
  isDarkMode,
  pagination,
  filters,
  profiles,
  totalPages = 1,
  totalAssociations = 0,
  searchUserId = "",
  onDeleteAssociation,
  onPageChange,
  onFilterChange,
  onClearFilters,
  onSearchByUser
}: AssociationViewTabProps) => {
  const [associationsExpanded, setAssociationsExpanded] = useState(true);
  const [localSearchUserId, setLocalSearchUserId] = useState(searchUserId);
  const [textConfig, setTextConfig] = useState<any>({});

  const fetchTextConfig = async () => {
    try {
      const response = await fetch("/text.json");
      const data = await response.json();
      setTextConfig(data);
    } catch (error) {
    }
  };

  useEffect(() => {
    fetchTextConfig();
  }, []);

  useEffect(() => {
    setLocalSearchUserId(searchUserId);
  }, [searchUserId]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (localSearchUserId.trim()) {
      onSearchByUser(localSearchUserId.trim());
    }
  };

  const handleClearSearch = () => {
    setLocalSearchUserId("");
    onClearFilters();
  };

  const hasActiveFilters = filters.profile_id;

  return (
    <div className="mt-4">
      <InfoBox isDark={isDarkMode} variant="success">
        <Info 
          size={isMobile ? 22 : 40} 
          className={`mr-2 ${isDarkMode ? "text-green-400" : "text-green-600"}`} 
        />
        <p className={`text-sm ${isDarkMode ? "text-green-100" : "text-green-800"}`}>
          {textConfig.ASSOCIATION_VIEW_INFO_TEXT}
        </p>
      </InfoBox>

             {/* 
             <div className="mb-4 space-y-3">
         <form onSubmit={handleSearch} className="space-y-3">
           <div>
                           <label 
                htmlFor="user-search" 
                className={`block text-sm font-medium mb-2 ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                {textConfig.ASSOCIATION_VIEW_SEARCH_LABEL || "Buscar por usuario"}
              </label>
             <div className="relative">
                               <input
                  id="user-search"
                  type="text"
                  value={localSearchUserId}
                  onChange={(e) => setLocalSearchUserId(e.target.value)}
                  placeholder={textConfig.ASSOCIATION_VIEW_SEARCH_PLACEHOLDER || "Ingrese el ID del usuario..."}
                 className={`w-full px-3 py-2 pl-10 rounded-md border-2 border-[#1BCF66] transition-colors ${
                   isDarkMode 
                     ? "bg-[#2D2D2D] text-white placeholder-gray-400 focus:border-[#1BCF66] focus:ring-1 focus:ring-[#1BCF66]" 
                     : "bg-white text-gray-900 placeholder-gray-500 focus:border-[#1BCF66] focus:ring-1 focus:ring-[#1BCF66]"
                 }`}
               />
               <Search 
                 size={16} 
                 className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${
                   isDarkMode ? "text-gray-400" : "text-gray-500"
                 }`} 
               />
             </div>
           </div>
           <div className="flex gap-2">
                           <button
                type="submit"
                disabled={!localSearchUserId.trim()}
                className={`px-4 py-2 rounded-md transition-colors ${
                  localSearchUserId.trim()
                    ? "bg-[#1BCF66] text-white hover:bg-[#1BCF66]/90"
                    : "bg-gray-400 text-gray-600 cursor-not-allowed"
                }`}
              >
                                 {textConfig.ASSOCIATION_VIEW_SEARCH_BUTTON || "Buscar"}
              </button>
              {(localSearchUserId.trim() || hasActiveFilters) && (
               <button
                 type="button"
                 onClick={handleClearSearch}
                 className={`flex items-center gap-1 px-3 py-2 rounded-md text-sm transition-colors ${
                   isDarkMode 
                     ? "text-gray-400 hover:text-white border border-gray-600 hover:border-gray-500" 
                     : "text-gray-600 hover:text-gray-900 border border-gray-300 hover:border-gray-400"
                 }`}
               >
                 <X size={14} />
                                   {textConfig.ASSOCIATION_VIEW_CLEAR_BUTTON || "Limpiar"}
               </button>
             )}
           </div>
         </form>
       </div>
             */}

             <CollapsibleItemsList
         items={associations}
         isLoading={isLoading}
         isDarkMode={isDarkMode}
         itemsExpanded={associationsExpanded}
         setItemsExpanded={setAssociationsExpanded}
         onDelete={onDeleteAssociation}
         type="association"
         title={textConfig.ASSOCIATION_VIEW_TITLE}
         emptyMessage={textConfig.ASSOCIATION_VIEW_EMPTY_MESSAGE}
         loadingMessage={textConfig.ASSOCIATION_VIEW_LOADING_MESSAGE}
         totalCount={totalAssociations}
         renderItem={(association, index) => (
           <AssociationItem
             association={association}
             isDarkMode={isDarkMode}
             isMobile={isMobile}
             onDelete={onDeleteAssociation}
           />
         )}
       />

       {(associations.length > 0 || pagination.currentPage > 1) && (
         <div className="mt-6">
           <Pagination
             currentPage={pagination.currentPage}
             totalPages={totalPages}
             isLoading={pagination.isLoading || isLoading}
             onPageChange={onPageChange}
           />
         </div>
       )}
    </div>
  );
};

export default AssociationViewTab;